import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAutoFitText } from '@/hooks/useAutoFitText';
import { useState } from 'react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import ManagedVideo from '@/components/common/ManagedVideo';
import ImageModal from '@/components/common/ImageModal';
import { preparedBadgeText } from './CharDetailsSections.helpers';
import {
  BookOpen,
  CheckCircle,
  Coins,
  Crown,
  Hand,
  Package,
  Shield,
  Shirt,
  Sparkles,
  Sword,
  User,
} from 'lucide-react';
import type { ReactNode } from 'react';

export interface SlotItem {
  name: string;
  emoji?: string;
  type: string;
  bonus?: string;
  damage?: string;
  ac?: number;
  properties: string[];
}

export interface EquippedSlotsData {
  mainHand?: SlotItem;
  offHand?: SlotItem;
  armor?: SlotItem;
  wondrousItems: SlotItem[];
  twoHandedEquipped: boolean;
}

export interface InventoryDisplayItem {
  name: string;
  emoji?: string;
  type: string;
  quantity: number;
  equipped: boolean;
  damage?: string;
  ac?: number;
  bonus?: string;
  description?: string;
  /** Set when the item is magical/rare (attack_bonus > 0, attunement, or item_kind === 'rare'). */
  is_magic?: boolean;
}

export interface SpellSlot {
  level: number;
  available: number;
  max: number;
}

export interface FeatureItem {
  name: string;
  emoji?: string;
  source: string;
  uses: string;
  description?: string;
}

export interface SpellbookSpell {
  name: string;
  emoji?: string;
  summary?: string;
}

export interface SpellbookLevel {
  level: number;
  spells: (string | SpellbookSpell)[];
}

export interface SectionWrapperProps {
  title: ReactNode;
  badge?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
  showSeparator: boolean;
}

export const SectionWrapper = ({
  title,
  badge,
  expanded,
  onToggle,
  children,
  showSeparator,
}: SectionWrapperProps) => (
  <>
    <div className="space-y-3">
      <button onClick={onToggle} className="flex items-center justify-between w-full text-left">
        <h3 className="font-semibold flex items-center gap-2">{title}</h3>
        {badge}
      </button>
      {expanded && children}
    </div>
    {showSeparator && <Separator />}
  </>
);

const equipmentIcon = (itemKind: string) => {
  switch (itemKind) {
    case 'weapon':
      return <Sword className="h-4 w-4 text-red-500" />;
    case 'armor':
      return <Shield className="h-4 w-4 text-blue-500" />;
    case 'clothing':
      return <Shirt className="h-4 w-4 text-purple-500" />;
    case 'wondrous_item':
      return <Sparkles className="h-4 w-4 text-violet-500" />;
    default:
      return <Package className="h-4 w-4 text-yellow-500" />;
  }
};

function AutoFitName({ name }: { name: string }) {
  const ref = useAutoFitText<HTMLHeadingElement>(name);
  return (
    <h3
      ref={ref}
      className="font-semibold leading-none tracking-tight font-character whitespace-nowrap"
    >
      {name}
    </h3>
  );
}

export function HeaderCard({
  characterName,
  subtitle,
  avatar,
  avatarEmoji,
  portraitUrl,
  children,
  isExpanded,
  onToggleExpanded,
}: {
  characterName: string;
  subtitle?: string;
  avatar?: string;
  avatarEmoji?: string;
  portraitUrl?: string;
  children: ReactNode;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}) {
  const [portraitOpen, setPortraitOpen] = useState(false);
  return (
    <Card className={isExpanded ? 'h-full overflow-y-auto' : 'h-auto'}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Avatar
              className={`h-6 w-6 shrink-0 ${portraitUrl ? 'cursor-pointer' : ''}`}
              onClick={portraitUrl ? () => setPortraitOpen(true) : undefined}
            >
              <AvatarImage src={avatar} />
              <AvatarFallback className="text-sm">
                {avatarEmoji ||
                  characterName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 overflow-hidden">
              <AutoFitName name={characterName} />
              {subtitle && (
                <p className="text-xs text-muted-foreground truncate uppercase tracking-wide">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={onToggleExpanded}
            aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && <CardContent className="space-y-4">{children}</CardContent>}
      <ImageModal
        isOpen={portraitOpen}
        onClose={() => setPortraitOpen(false)}
        imageUrl={portraitUrl}
        altText={characterName}
      />
    </Card>
  );
}

export function PortraitSection({
  portraitUrl,
  portraitType,
  characterName,
  expanded,
  onToggle,
  showSeparator,
  isGenerating,
}: {
  portraitUrl: string | null;
  portraitType: 'image' | 'video' | null;
  characterName: string;
  expanded: boolean;
  onToggle: () => void;
  showSeparator: boolean;
  isGenerating?: boolean;
}) {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const canEnlarge = !!portraitUrl && !isGenerating;

  return (
    <SectionWrapper
      title={
        <>
          <User className="h-4 w-4 text-emerald-500" />
          Portrait
        </>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div
        className={`relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-muted/50 ${
          canEnlarge ? 'cursor-pointer' : ''
        }`}
        onClick={canEnlarge ? () => setOverlayOpen(true) : undefined}
      >
        {isGenerating ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
            <div className="flex flex-col items-center gap-2">
              <AnkhSpinner size="lg" className="text-accent-gold" />
              <span className="text-sm text-muted-foreground">Generating portrait...</span>
            </div>
          </div>
        ) : !portraitUrl ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
            <div className="flex flex-col items-center gap-2">
              <User className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Portrait loading...</span>
            </div>
          </div>
        ) : portraitType === 'video' ? (
          <ManagedVideo src={portraitUrl} className="w-full h-full object-cover" />
        ) : (
          <img
            src={portraitUrl}
            alt={`${characterName} portrait`}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <ImageModal
        isOpen={overlayOpen}
        onClose={() => setOverlayOpen(false)}
        imageUrl={portraitType === 'image' ? (portraitUrl ?? undefined) : undefined}
        videoUrl={portraitType === 'video' ? (portraitUrl ?? undefined) : undefined}
        altText={`${characterName} portrait`}
      />
    </SectionWrapper>
  );
}

const slotIcon = (label: string) => {
  if (label === 'Main Hand' || label === 'Two-Handed')
    return <Sword className="h-4 w-4 text-red-500" />;
  if (label === 'Off Hand') return <Hand className="h-4 w-4 text-orange-500" />;
  if (label === 'Armor') return <Shield className="h-4 w-4 text-blue-500" />;
  if (label === 'Wondrous Item') return <Sparkles className="h-4 w-4 text-violet-500" />;
  return <Package className="h-4 w-4 text-yellow-500" />;
};

const SlotRow = ({ label, item }: { label: string; item?: SlotItem }) => (
  <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
    <div className="flex items-center gap-2">
      {slotIcon(label)}
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {item ? (
          <>
            <div className="text-sm font-medium">
              {item.emoji && <span className="mr-0.5">{item.emoji}</span>}
              {item.name}
            </div>
            {(item.bonus || item.damage || item.ac || item.properties.length > 0) && (
              <div className="text-xs text-muted-foreground">
                {item.bonus && `${item.bonus} `}
                {item.damage && `${item.damage} `}
                {item.ac != null && `AC ${item.ac} `}
                {item.properties.join(', ')}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground italic">Empty</div>
        )}
      </div>
    </div>
  </div>
);

export function EquippedSection({
  slots,
  expanded,
  onToggle,
  showSeparator,
}: {
  slots: EquippedSlotsData;
  expanded: boolean;
  onToggle: () => void;
  showSeparator: boolean;
}) {
  const itemCount =
    (slots.mainHand ? 1 : 0) +
    (slots.offHand ? 1 : 0) +
    (slots.armor ? 1 : 0) +
    slots.wondrousItems.length;

  return (
    <SectionWrapper
      title={
        <>
          <Sword className="h-4 w-4 text-red-500" />
          Equipped
        </>
      }
      badge={
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0 uppercase tracking-wide bg-muted/40 backdrop-blur-sm border-foreground/20"
        >
          {itemCount}
        </Badge>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div className="space-y-2">
        <SlotRow
          label={slots.twoHandedEquipped ? 'Two-Handed' : 'Main Hand'}
          item={slots.mainHand}
        />
        {!slots.twoHandedEquipped && <SlotRow label="Off Hand" item={slots.offHand} />}
        <SlotRow label="Armor" item={slots.armor} />
        {slots.wondrousItems.map((item, index) => (
          <SlotRow key={index} label="Wondrous Item" item={item} />
        ))}
      </div>
    </SectionWrapper>
  );
}

export function SpellSlotsSection({
  expanded,
  onToggle,
  spellSlots,
  showSeparator,
}: {
  expanded: boolean;
  onToggle: () => void;
  spellSlots: SpellSlot[];
  showSeparator: boolean;
}) {
  const totalAvailable = spellSlots.reduce((sum, s) => sum + s.available, 0);
  const totalMax = spellSlots.reduce((sum, s) => sum + s.max, 0);
  return (
    <SectionWrapper
      title={
        <>
          <Sparkles className="h-4 w-4 text-amber-500" />
          Spell Slots
        </>
      }
      badge={
        <Badge
          variant="outline"
          className="border-transparent text-[10px] px-1.5 py-0 uppercase tracking-wide bg-amber-500/15 backdrop-blur-sm text-foreground dark:text-amber-100"
        >
          {totalAvailable}/{totalMax}
        </Badge>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div className="space-y-1 pl-3">
        {spellSlots.map((slot, index) => (
          <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
            <span className="text-sm font-medium">Level {slot.level}</span>
            <Badge
              variant="outline"
              className="border-transparent text-[10px] px-1.5 py-0 uppercase tracking-wide bg-amber-500/15 backdrop-blur-sm text-foreground dark:text-amber-100"
            >
              {slot.available}/{slot.max}
            </Badge>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

export function PreparedSection({
  expanded,
  onToggle,
  preparedSpellNames,
  chosenPreparedCount,
  maxPreparedSpells,
  showSeparator,
}: {
  expanded: boolean;
  onToggle: () => void;
  preparedSpellNames: Array<string | { name: string; emoji?: string; summary?: string }>;
  // Count to display in the cap badge. Always-prepared spells (feat / domain /
  // subclass grants) per SRD 5.2.1 do NOT count against the prep cap, so when
  // `preparedSpellNames` is a union of session + always-prepared the caller
  // passes the chosen-only count here. Falls back to `preparedSpellNames.length`
  // for legacy callers that still flat-list session-only entries.
  chosenPreparedCount?: number;
  maxPreparedSpells?: number;
  showSeparator: boolean;
}) {
  const badgeText = preparedBadgeText(
    preparedSpellNames.length,
    chosenPreparedCount,
    maxPreparedSpells
  );
  return (
    <SectionWrapper
      title={
        <>
          <Sparkles className="h-4 w-4 text-violet-500" />
          Prepared Spells
        </>
      }
      badge={
        <Badge
          variant="outline"
          className="border-transparent text-[10px] px-1.5 py-0 uppercase tracking-wide bg-violet-500/15 backdrop-blur-sm text-foreground dark:text-violet-100"
        >
          {badgeText}
        </Badge>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      {preparedSpellNames.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {preparedSpellNames.map((spell, index) => {
            const name = typeof spell === 'string' ? spell : spell.name;
            const emoji = typeof spell === 'string' ? undefined : spell.emoji;
            const summary = typeof spell === 'string' ? undefined : spell.summary;
            const badge = (
              <Badge
                key={index}
                variant="outline"
                className="border-transparent text-xs bg-violet-500/15 backdrop-blur-sm text-foreground dark:text-violet-100 cursor-default"
              >
                {emoji && <span className="mr-0.5">{emoji}</span>}
                {name}
              </Badge>
            );
            if (!summary) return badge;
            return (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger asChild>{badge}</TooltipTrigger>
                  <TooltipContent className="max-w-64">{summary}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      )}
    </SectionWrapper>
  );
}

export function FeaturesSection({
  expanded,
  onToggle,
  feats,
  features,
  showSeparator,
}: {
  expanded: boolean;
  onToggle: () => void;
  feats: Array<{ name: string; emoji?: string; description?: string }>;
  features: FeatureItem[];
  showSeparator: boolean;
}) {
  return (
    <SectionWrapper
      title={
        <>
          <Crown className="h-4 w-4 text-amber-500" />
          Features, Traits & Feats
        </>
      }
      badge={
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0 uppercase tracking-wide bg-muted/40 backdrop-blur-sm border-foreground/20"
        >
          {features.length + feats.length}
        </Badge>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div className="overflow-y-auto max-h-[600px]">
        <div className="space-y-2">
          {feats.map((feat, index) => (
            <div
              key={`feat-${index}`}
              className="p-2 rounded bg-amber-500/10 backdrop-blur-sm border border-amber-500/20"
            >
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-medium">
                  {feat.emoji && <span className="mr-0.5">{feat.emoji}</span>}
                  {feat.name}
                </h4>
                <Badge
                  variant="outline"
                  className="border-transparent text-[8px] px-1.5 py-0 uppercase tracking-wide bg-amber-500/15 text-foreground dark:text-amber-100"
                >
                  Feat
                </Badge>
              </div>
              {feat.description && (
                <p className="text-xs text-muted-foreground">{feat.description}</p>
              )}
            </div>
          ))}
          {features.map((feature, index) => (
            <div
              key={`feature-${index}`}
              className="p-2 rounded bg-violet-500/10 backdrop-blur-sm border border-violet-500/20"
            >
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-medium">
                  {feature.emoji && <span className="mr-0.5">{feature.emoji}</span>}
                  {feature.name}
                </h4>
                <Badge
                  variant="outline"
                  className="border-transparent text-[8px] px-1.5 py-0 uppercase tracking-wide bg-violet-500/15 text-foreground dark:text-violet-100"
                >
                  {feature.source}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mb-1">{feature.uses}</div>
              <p className="text-xs text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
}

function formatGold(goldCp: number): string {
  const safe = Math.max(0, Math.floor(goldCp));
  if (safe === 0) return '0 GP';
  const gp = Math.floor(safe / 100);
  const cp = safe % 100;
  if (gp > 0 && cp > 0) return `${gp} GP ${cp} CP`;
  if (gp > 0) return `${gp} GP`;
  return `${cp} CP`;
}

export function InventorySection({
  expanded,
  onToggle,
  items,
  goldCp,
  showSeparator,
}: {
  expanded: boolean;
  onToggle: () => void;
  items: InventoryDisplayItem[];
  goldCp?: number;
  showSeparator: boolean;
}) {
  return (
    <SectionWrapper
      title={
        <>
          <Package className="h-4 w-4 text-yellow-500" />
          Inventory
        </>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div className="space-y-2">
        {goldCp != null && (
          <div className="p-2 bg-muted/50 rounded">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-yellow-500" />
                <div className="text-sm font-medium">Coins</div>
              </div>
              <Badge
                variant="outline"
                className="border-transparent text-[10px] px-1.5 py-0 uppercase tracking-wide bg-yellow-500/40 backdrop-blur-sm text-foreground dark:text-yellow-100"
              >
                {formatGold(goldCp)}
              </Badge>
            </div>
          </div>
        )}
        {items.map((item, index) => (
          <div key={index} className="p-2 bg-muted/50 rounded">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {item.emoji ? (
                  <span className="text-base flex-shrink-0">{item.emoji}</span>
                ) : (
                  equipmentIcon(item.type)
                )}
                <div>
                  <div className="text-sm font-medium">
                    {item.name}
                    {item.quantity > 1 && (
                      <span className="text-muted-foreground font-normal"> x{item.quantity}</span>
                    )}
                  </div>
                  {(item.bonus || item.damage || item.ac != null) && (
                    <div className="text-xs text-muted-foreground">
                      {item.bonus && `${item.bonus} `}
                      {item.damage && `${item.damage} `}
                      {item.ac != null && `AC ${item.ac}`}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {item.equipped && <CheckCircle className="h-3 w-3 text-green-500" />}
                {item.type !== 'mundane' && (
                  <Badge
                    variant="outline"
                    className={
                      item.is_magic
                        ? 'border-transparent text-[8px] px-1.5 py-0 uppercase tracking-wide bg-violet-500/15 backdrop-blur-sm text-foreground dark:text-violet-100 inline-flex items-center gap-1'
                        : 'text-[8px] px-1.5 py-0 uppercase tracking-wide bg-muted/40 backdrop-blur-sm border-foreground/20'
                    }
                  >
                    {item.is_magic && <Sparkles className="h-3 w-3" />}
                    {item.type.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
            </div>
            {item.description && (
              <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
            )}
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

export function SpellbookSection({
  expanded,
  onToggle,
  allKnownSpells,
  showSeparator,
}: {
  expanded: boolean;
  onToggle: () => void;
  allKnownSpells: SpellbookLevel[];
  showSeparator: boolean;
}) {
  return (
    <SectionWrapper
      title={
        <>
          <BookOpen className="h-4 w-4 text-sky-500" />
          Spellbook
        </>
      }
      badge={
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0 uppercase tracking-wide bg-muted/40 backdrop-blur-sm border-foreground/20"
        >
          {allKnownSpells.reduce((sum, lvl) => sum + lvl.spells.length, 0)}
        </Badge>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div className="space-y-3">
        {allKnownSpells.map((level) => (
          <div key={level.level} className="p-2 bg-muted/50 rounded">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Level {level.level}</h4>
              <Badge
                variant="outline"
                className="border-transparent text-[10px] px-1.5 py-0 uppercase tracking-wide bg-sky-500/15 backdrop-blur-sm text-foreground dark:text-sky-100"
              >
                {level.spells.length}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1">
              {level.spells.map((spell, index) => {
                const name = typeof spell === 'string' ? spell : spell.name;
                const emoji = typeof spell === 'string' ? undefined : spell.emoji;
                const summary = typeof spell === 'string' ? undefined : spell.summary;
                const badge = (
                  <Badge
                    key={index}
                    variant="outline"
                    className="border-transparent text-xs bg-sky-500/15 backdrop-blur-sm text-foreground dark:text-sky-100 cursor-default"
                  >
                    {emoji && <span className="mr-0.5">{emoji}</span>}
                    {name}
                  </Badge>
                );
                if (!summary) return badge;
                return (
                  <TooltipProvider key={index}>
                    <Tooltip>
                      <TooltipTrigger asChild>{badge}</TooltipTrigger>
                      <TooltipContent className="max-w-64">{summary}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}
